import React from 'react';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';
import type { CountryConfigurationUrlsLocalizedLinkUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrlsLocalizedLinkUrls';
import type { SeoLocationDetailsFacts } from '@rover/rsdk/src/apiClient/latest/model/seoLocationDetailsFacts';
import type { LocalityType } from '@rover/types/src/LocalityType';

import FirstFAQIsOpen from '../../../../ServiceLanding/components/ServiceFaqs/FirstFAQIsOpen';
import FrequentlyAskedQuestion from '../../../../ServiceLanding/components/ServiceFaqs/FrequentlyAskedQuestion';
import UnorderedList, {
  ListItem,
} from '../../../../ServiceLanding/components/ServiceFaqs/UnorderedList';

export type ServiceFaq = {
  facts: SeoLocationDetailsFacts;
  locationName: string;
  localizedLinkUrls: CountryConfigurationUrlsLocalizedLinkUrls;
  localityType: LocalityType;
  htmlRef?: React.RefObject<HTMLElement | null>;
};

type TrainingFAQ = Pick<ServiceFaq, 'localizedLinkUrls' | 'locationName' | 'htmlRef'>;

// TODO DEV-104195: Translate all text below

const DogTrainingFAQ: React.FC<TrainingFAQ> = ({
  localizedLinkUrls: links,
  locationName,
  htmlRef,
}) => {
  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="dog-training-faqs"
      mt={['6x', '16x']}
      pb={['4x', '8x']}
      showImage={false}
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        Dog Training FAQs
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        Frequently asked questions about Rover
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        <FrequentlyAskedQuestion
          question={`How do trainers in ${locationName} get approved to list on Rover?`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            Trainers listing on Rover must be credentialed dog trainers, commit to reward-based
            training methods, and pass a background check to offer dog training in {locationName}.
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 2 */}
        <FrequentlyAskedQuestion question="What is reward-based training?">
          <>
            <Paragraph webProps={{ maxWidth: 'none' }}>
              Reward-based training encourages dogs to offer desired behaviors by reinforcing the
              behavior with something the dog likes; treats, play, or toys. Reward-based training
              prioritizes using the least intrusive intervention; it promotes thinking rather than
              punishing mistakes.
            </Paragraph>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              When rewards are used, the likelihood the behavior is repeated increases. The positive
              connection to the reward results in making learning enjoyable, strengthens the human
              animal bond and over time leads to consistent desired behaviors.
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 3 */}
        <FrequentlyAskedQuestion question={`Where do training sessions in ${locationName} happen?`}>
          <>
            <Paragraph webProps={{ maxWidth: 'none' }}>
              Training can happen many places — your home, a park, a store, or anywhere else that
              you and your trainer decide is best for the training you are doing! Many trainers will
              recommend training in your home. This allows them to observe your dog in their natural
              environment so they can effectively assess and best help reach your training goals.
            </Paragraph>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              Trainers will often mention on their profile where they typically hold training
              sessions, but you can always contact a trainer to learn more!
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 4 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion
              question={`How do I contact a trainer in ${locationName} for dog training?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  If you’re searching for a dog trainer in {locationName} for the first time, visit
                  the trainer’s profile and select the Contact button.
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  If you have an active request or have booked a service with a trainer before,
                  learn more about how to do this in the{' '}
                  <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                    Rover app
                  </Link>{' '}
                  or on your{' '}
                  <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                    computer
                  </Link>
                  .
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={`What sort of dog training services do trainers in ${locationName} offer on Rover?`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            Rover makes it easy to find {locationName} dog trainers to provide loving care from
            their own home. The background-checked, 5-star trainers you can find on Rover will work
            with you and your dog to build new skills and address behaviors. Dog trainers can work
            on:
            <UnorderedList>
              <ListItem>
                Puppy training, adult dog training, agility training, clicker training and advanced
                training.
              </ListItem>

              <ListItem>
                Behaviors such as barking, leash manners, leash reactivity, house manners, jumping,
                fearful behavior, anxious behavior, separation distress, recall, biting, chewing,
                mouthiness, potty training and resource guarding.
              </ListItem>
            </UnorderedList>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={`Can I contact multiple dog trainers in ${locationName}?`}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }}>
              Of course! Having a trainer that is the right fit for both you and your pup is an
              important step to reaching your training goals. We recommend reaching out to any
              trainer that interests you. Your initial request is not final until both you and the
              trainer decide you want to move forward with booking.
            </Paragraph>

            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              Also, trainers may offer or require consultations. Make sure to ask trainers so you
              can make the best decision for you and your pet.
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        <FrequentlyAskedQuestion
          question={`My dog has advanced training needs. Can dog trainers in ${locationName} help me?`}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }}>
              Many trainers on Rover indicate experience in a wide range of behaviors, from puppy
              training to more advanced behaviors and training types.
            </Paragraph>

            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              Review a trainer’s profile to see if their experience aligns with your needs, and if
              you have questions, don’t hesitate to contact them to ensure they’re the right trainer
              for you and your pet.
            </Paragraph>
          </>
        </FrequentlyAskedQuestion>

        {/* 8 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion question="How do I cancel a dog training service and receive a refund?">
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  We get it—sometimes plans change and you may need to cancel your dog training
                  service. You can cancel an upcoming booking directly from the{' '}
                  <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                    Rover app
                  </Link>{' '}
                  or your{' '}
                  <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                    computer
                  </Link>
                  , in accordance with your trainer’s cancellation policy.
                </Paragraph>

                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  Note: If your dog trainer requested to cancel this booking, then they need to
                  cancel from their account. Please contact them via{' '}
                  <Link href="/account/inbox/" target="_blank">
                    Rover Messages
                  </Link>
                  .
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default DogTrainingFAQ;
